/**
 * AuthLayout — Full-screen auth wrapper with AWENI branding.
 * Glass-morphism card, gradient background, language selector.
 * Mobile-first, RTL-aware, WCAG AA compliant.
 */

import type { AweniContext } from '../../../context/AweniContext';

const LANG_FLAGS: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  ar: 'AR',
  es: 'ES',
  de: 'DE',
};

export function createAuthLayout(ctx: AweniContext): {
  wrapper: HTMLElement;
  card: HTMLElement;
  setTitle: (title: string) => void;
  setSubtitle: (subtitle: string) => void;
  showError: (msg: string) => void;
  clearError: () => void;
  showSuccess: (msg: string) => void;
  clearMessages: () => void;
} {
  // Inject styles once
  _injectAuthStyles();

  const wrapper = document.createElement('div');
  wrapper.className = 'auth-layout';
  wrapper.setAttribute('dir', ctx.i18n.dir);

  // Background decoration
  const bgDecor = document.createElement('div');
  bgDecor.className = 'auth-bg-decor';
  wrapper.appendChild(bgDecor);

  // Content container (scrollable)
  const content = document.createElement('div');
  content.className = 'auth-content';

  // Logo + brand
  const brand = document.createElement('div');
  brand.className = 'auth-brand';
  brand.innerHTML = `
    <div class="auth-logo">
      <img src="/aweni/icons/pwa-192x192.png" alt="AWENI" width="56" height="56" style="display:block;border-radius:14px;background:#FAF7F2;" />
      <!-- Brand v1: real monogram instead of legacy A-on-gradient placeholder. -->
    </div>
    <div class="auth-brand-name">AWENI</div>
  `;
  content.appendChild(brand);

  // Title
  const titleEl = document.createElement('h1');
  titleEl.className = 'auth-title';
  content.appendChild(titleEl);

  // Subtitle
  const subtitleEl = document.createElement('p');
  subtitleEl.className = 'auth-subtitle';
  content.appendChild(subtitleEl);

  // Message area (errors/success)
  const messageEl = document.createElement('div');
  messageEl.className = 'auth-message';
  messageEl.setAttribute('role', 'alert');
  messageEl.setAttribute('aria-live', 'polite');
  content.appendChild(messageEl);

  // Card (form container)
  const card = document.createElement('div');
  card.className = 'auth-card';
  content.appendChild(card);

  // Language selector at bottom
  const langBar = document.createElement('div');
  langBar.className = 'auth-lang-bar';
  for (const locale of ctx.i18n.supportedLocales) {
    const btn = document.createElement('button');
    btn.className = `auth-lang-btn${locale === ctx.i18n.locale ? ' active' : ''}`;
    btn.textContent = LANG_FLAGS[locale] || locale.toUpperCase();
    btn.setAttribute('aria-label', `Switch to ${locale}`);
    btn.addEventListener('click', () => {
      ctx.i18n.setLocale(locale);
      wrapper.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr');
      // Update active state
      langBar.querySelectorAll('.auth-lang-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
    langBar.appendChild(btn);
  }
  content.appendChild(langBar);

  wrapper.appendChild(content);

  return {
    wrapper,
    card,
    setTitle(title: string) {
      titleEl.textContent = title;
    },
    setSubtitle(subtitle: string) {
      subtitleEl.textContent = subtitle;
      subtitleEl.style.display = subtitle ? '' : 'none';
    },
    showError(msg: string) {
      messageEl.innerHTML = `<div class="auth-msg auth-msg-error">${_escapeHtml(msg)}</div>`;
    },
    clearError() {
      const errEl = messageEl.querySelector('.auth-msg-error');
      if (errEl) errEl.remove();
    },
    showSuccess(msg: string) {
      messageEl.innerHTML = `<div class="auth-msg auth-msg-success">${_escapeHtml(msg)}</div>`;
    },
    clearMessages() {
      messageEl.innerHTML = '';
    },
  };
}

function _escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function _injectAuthStyles(): void {
  if (document.getElementById('aweni-auth-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-auth-styles';
  style.textContent = `
    .auth-layout {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--bg-primary, #0F172A);
      overflow: hidden;
    }

    .auth-bg-decor {
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 60% 50% at 20% 20%, rgba(10, 10, 10, 0.12), transparent),
        radial-gradient(ellipse 50% 40% at 80% 80%, rgba(59, 130, 246, 0.10), transparent);
      pointer-events: none;
    }

    .auth-content {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: 420px;
      padding: 24px 20px;
      padding-top: max(24px, env(safe-area-inset-top, 24px));
      padding-bottom: max(24px, env(safe-area-inset-bottom, 24px));
      overflow-y: auto;
      max-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .auth-brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      margin-bottom: 24px;
      animation: authFadeIn 400ms ease-out;
    }

    .auth-logo {
      filter: drop-shadow(0 4px 12px rgba(10, 10, 10, 0.3));
    }

    .auth-brand-name {
      font-size: 22px;
      font-weight: 800;
      letter-spacing: 2px;
      color: var(--text-primary, #F1F5F9);
    }

    .auth-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--text-primary, #F1F5F9);
      text-align: center;
      margin: 0 0 4px;
      animation: authFadeIn 400ms ease-out 100ms both;
    }

    .auth-subtitle {
      font-size: 0.875rem;
      color: var(--text-secondary, #94A3B8);
      text-align: center;
      margin: 0 0 16px;
      animation: authFadeIn 400ms ease-out 150ms both;
    }

    .auth-message {
      width: 100%;
      min-height: 0;
    }

    .auth-msg {
      padding: 10px 14px;
      border-radius: 10px;
      font-size: 0.85rem;
      margin-bottom: 12px;
      animation: authSlideDown 200ms ease-out;
    }

    .auth-msg-error {
      background: rgba(239, 68, 68, 0.12);
      color: #FCA5A5;
      border: 1px solid rgba(239, 68, 68, 0.2);
    }

    .auth-msg-success {
      background: rgba(10, 10, 10, 0.12);
      color: #FAF7F2;
      border: 1px solid rgba(10, 10, 10, 0.2);
    }

    .auth-card {
      width: 100%;
      background: rgba(30, 41, 59, 0.6);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 20px;
      padding: 24px;
      animation: authFadeIn 400ms ease-out 200ms both;
    }

    .auth-lang-bar {
      display: flex;
      gap: 8px;
      margin-top: 24px;
      animation: authFadeIn 400ms ease-out 300ms both;
    }

    .auth-lang-btn {
      padding: 6px 14px;
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: transparent;
      color: var(--text-secondary, #94A3B8);
      font-size: 0.75rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 150ms ease;
      min-height: 36px;
      min-width: 44px;
    }

    .auth-lang-btn:hover,
    .auth-lang-btn:focus-visible {
      background: rgba(255, 255, 255, 0.06);
      color: var(--text-primary, #F1F5F9);
    }

    .auth-lang-btn.active {
      background: rgba(10, 10, 10, 0.15);
      color: #0A0A0A;
      border-color: rgba(10, 10, 10, 0.3);
    }

    /* ---- Form elements ---- */

    .auth-field {
      margin-bottom: 16px;
    }

    .auth-label {
      display: block;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--text-secondary, #94A3B8);
      margin-bottom: 6px;
    }

    .auth-input-wrap {
      position: relative;
    }

    .auth-input {
      width: 100%;
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(15, 23, 42, 0.5);
      color: var(--text-primary, #F1F5F9);
      font-size: 0.95rem;
      outline: none;
      transition: border-color 150ms ease, box-shadow 150ms ease;
      box-sizing: border-box;
    }

    .auth-input::placeholder {
      color: rgba(148, 163, 184, 0.5);
    }

    .auth-input:focus {
      border-color: rgba(10, 10, 10, 0.5);
      box-shadow: 0 0 0 3px rgba(10, 10, 10, 0.1);
    }

    .auth-input.error {
      border-color: rgba(239, 68, 68, 0.5);
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }

    .auth-field-error {
      font-size: 0.75rem;
      color: #FCA5A5;
      margin-top: 4px;
    }

    .auth-toggle-pw {
      position: absolute;
      inset-inline-end: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: var(--text-secondary, #94A3B8);
      cursor: pointer;
      padding: 4px;
      min-width: 44px;
      min-height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .auth-btn {
      width: 100%;
      padding: 14px;
      border-radius: 12px;
      border: none;
      font-size: 1rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 150ms ease;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .auth-btn-primary {
      background: linear-gradient(135deg, #0A0A0A, #2A2520);
      color: #fff;
    }

    .auth-btn-primary:hover:not(:disabled) {
      filter: brightness(1.1);
      transform: translateY(-1px);
    }

    .auth-btn-primary:active:not(:disabled) {
      transform: translateY(0);
    }

    .auth-btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .auth-btn-secondary {
      background: transparent;
      color: var(--text-secondary, #94A3B8);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .auth-link {
      color: #0A0A0A;
      text-decoration: none;
      font-size: 0.85rem;
      font-weight: 500;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
    }

    .auth-link:hover {
      text-decoration: underline;
    }

    .auth-divider {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 16px 0;
      color: var(--text-secondary, #64748B);
      font-size: 0.8rem;
    }

    .auth-divider::before,
    .auth-divider::after {
      content: '';
      flex: 1;
      height: 1px;
      background: rgba(255, 255, 255, 0.08);
    }

    .auth-footer-row {
      text-align: center;
      margin-top: 16px;
      font-size: 0.85rem;
      color: var(--text-secondary, #94A3B8);
    }

    /* ---- Password strength ---- */

    .auth-pw-strength {
      display: flex;
      gap: 4px;
      margin-top: 8px;
    }

    .auth-pw-bar {
      flex: 1;
      height: 4px;
      border-radius: 2px;
      background: rgba(255, 255, 255, 0.08);
      transition: background 200ms ease;
    }

    .auth-pw-label {
      font-size: 0.72rem;
      margin-top: 4px;
      text-align: end;
    }

    /* ---- Spinner ---- */

    .auth-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid transparent;
      border-top-color: currentColor;
      border-radius: 50%;
      animation: authSpin 600ms linear infinite;
    }

    /* ---- Animations ---- */

    @keyframes authFadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes authSlideDown {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes authSpin {
      to { transform: rotate(360deg); }
    }

    /* ---- Responsive ---- */

    @media (max-width: 480px) {
      .auth-content {
        padding: 16px 16px;
        padding-top: max(16px, env(safe-area-inset-top, 16px));
      }
      .auth-card {
        padding: 20px 16px;
        border-radius: 16px;
      }
      .auth-title {
        font-size: 1.25rem;
      }
    }

    /* ---- Light theme overrides ---- */

    [data-theme="light"] .auth-layout,
    :root:not(.dark) .auth-layout {
      background: #F8FAFC;
    }

    [data-theme="light"] .auth-bg-decor,
    :root:not(.dark) .auth-bg-decor {
      background:
        radial-gradient(ellipse 60% 50% at 20% 20%, rgba(10, 10, 10, 0.06), transparent),
        radial-gradient(ellipse 50% 40% at 80% 80%, rgba(59, 130, 246, 0.05), transparent);
    }

    [data-theme="light"] .auth-card,
    :root:not(.dark) .auth-card {
      background: rgba(255, 255, 255, 0.85);
      border-color: rgba(0, 0, 0, 0.08);
    }

    [data-theme="light"] .auth-input,
    :root:not(.dark) .auth-input {
      background: rgba(255, 255, 255, 0.8);
      border-color: rgba(0, 0, 0, 0.1);
      color: #1E293B;
    }

    /* ---- Turnstile ---- */

    .auth-turnstile {
      display: flex;
      justify-content: center;
      margin: 12px 0;
      min-height: 65px;
    }

    /* ---- Check your email screen ---- */

    .auth-check-email {
      text-align: center;
      padding: 16px 0;
    }

    .auth-check-email-icon {
      margin: 0 auto 16px;
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: rgba(10, 10, 10, 0.12);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .auth-check-email h2 {
      font-size: 1.2rem;
      font-weight: 700;
      color: var(--text-primary, #F1F5F9);
      margin: 0 0 8px;
    }

    .auth-check-email p {
      font-size: 0.9rem;
      color: var(--text-secondary, #94A3B8);
      margin: 0 0 20px;
      line-height: 1.5;
    }
  `;
  document.head.appendChild(style);
}
